import { memo } from 'react';
import { Button, IconButton } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Tray } from '@cbhq/cds-web/overlays/tray/Tray';
import { PageFooter } from '@cbhq/cds-web/page/PageFooter';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';

const messages = defineMessages({
  title: {
    id: 'Security.claimADomain.addDomainTrayTitle',
    defaultMessage: 'Add a domain',
    description: 'Title of the tray shown when a user tries to add a domain',
  },
  closeAccessibilityLabel: {
    id: 'Security.claimADomain.addDomainTrayCloseAccessibilityLabel',
    defaultMessage: 'Close add a domain tray',
    description: 'Accessibility label for the add domain tray close button',
  },
  body: {
    id: 'Security.claimADomain.addDomainTrayBody',
    defaultMessage:
      'Domain claims are for select customers at this time. If you need to add another domain for your organization, please contact your account manager to begin the process.',
    description: 'Body of the tray shown when a user tries to add a domain',
  },
  dismiss: {
    id: 'Security.claimADomain.addDomainTrayDismiss',
    defaultMessage: 'Got it',
    description: 'Dismiss button label for the add domain tray',
  },
});

export type AddDomainTrayProps = {
  onClose: () => void;
};

const TRAY_STYLES = { container: { width: '532px' } } as const;

// Tray's own animated close (via a ref's imperative `close()`, or its
// built-in ESC/backdrop-dismiss) relies on an internal framer-motion promise
// that has proven unreliable here — it silently no-ops instead of ever
// calling onCloseComplete, leaving the tray stuck open. Calling onClose
// directly sidesteps that mechanism entirely: no exit animation, but the
// tray is guaranteed to actually close.
export const AddDomainTray = memo(function AddDomainTray({ onClose }: AddDomainTrayProps) {
  const { formatMessage } = useIntl();
  const title = formatMessage(messages.title);

  return (
    <Tray
      accessibilityLabel={title}
      hideCloseButton
      preventDismiss
      onCloseComplete={onClose}
      pin="right"
      styles={TRAY_STYLES}
      title={
        <HStack width="100%" justifyContent="flex-end">
          <IconButton
            transparent
            color="fg"
            name="close"
            onClick={onClose}
            accessibilityLabel={formatMessage(messages.closeAccessibilityLabel)}
            data-dd-action-name="security_add_domain_close"
          />
        </HStack>
      }
      footer={
        <PageFooter
          borderedTop
          height="auto"
          justifyContent="flex-end"
          action={
            <Button
              variant="primary"
              compact
              onClick={onClose}
              data-dd-action-name="security_add_domain_dismiss"
              testID="add-domain-tray-dismiss-button"
            >
              {formatMessage(messages.dismiss)}
            </Button>
          }
        />
      }
    >
      <VStack width="100%" gap={2}>
        <Box
          width={40}
          height={40}
          borderRadius={400}
          background="bgPrimaryWash"
          alignItems="center"
          justifyContent="center"
        >
          <Icon name="info" color="fgPrimary" size="m" />
        </Box>
        <Text as="p" font="title3" color="fg">
          {title}
        </Text>
        <Text as="p" font="body" color="fgMuted">
          {formatMessage(messages.body)}
        </Text>
      </VStack>
    </Tray>
  );
});
